import * as React from "react";
import { BACKEND } from "@/lib/api";

/**
 * Subscribes to the SSE stream for a company. Uses fetch with credentials so
 * the HttpOnly cookie is sent (EventSource can't set credentials cross-site
 * in the same way; using fetch + ReadableStream is universal).
 *
 * On drop, auto-reconnects with exponential backoff (1s → 2s → 4s … max 15s).
 * On the tab regaining focus we force an immediate reconnect + fire a
 * `fable:refetch` window event so pages can refresh stale caches.
 *
 * onEvent(evt): called for every server event (envelope shape from realtime.py)
 *   {event_id, type, module, payload, ts, company_id, actor_id}
 * Returns { connected } state.
 */
export function useCompanyEvents(companyId, onEvent) {
  const [connected, setConnected] = React.useState(false);
  const cbRef = React.useRef(onEvent);
  cbRef.current = onEvent;

  React.useEffect(() => {
    if (!companyId) return () => {};
    let cancelled = false;
    let controller = null;
    let attempt = 0;
    let reconnectTimer = null;

    const nextBackoff = () => {
      const base = Math.min(15000, 1000 * Math.pow(2, Math.min(attempt, 4)));
      // jitter ±20%
      return Math.round(base * (0.8 + Math.random() * 0.4));
    };

    const run = async () => {
      if (cancelled) return;
      controller = new AbortController();
      try {
        const res = await fetch(
          `${BACKEND}/api/companies/${companyId}/events`,
          { credentials: "include", signal: controller.signal,
            headers: { Accept: "text/event-stream" } }
        );
        if (!res.ok || !res.body) throw new Error(`sse status ${res.status}`);
        setConnected(true);
        attempt = 0;
        const reader = res.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buf = "";
        while (!cancelled) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let idx;
          while ((idx = buf.indexOf("\n\n")) !== -1) {
            const raw = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            const lines = raw.split("\n");
            const dataLine = lines.find((l) => l.startsWith("data:"));
            if (!dataLine) continue;
            try {
              const parsed = JSON.parse(dataLine.slice(5).trim());
              cbRef.current && cbRef.current(parsed);
            } catch { /* ignore malformed */ }
          }
        }
      } catch (_e) {
        /* network drop / abort */
      } finally {
        if (!cancelled) {
          setConnected(false);
          attempt += 1;
          reconnectTimer = setTimeout(run, nextBackoff());
        }
      }
    };

    // Force reconnect + notify pages on tab focus.
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      // If disconnected, clear timer and reconnect immediately.
      if (!connected && reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
        run();
      }
      // Tell pages to re-fetch anything stale.
      window.dispatchEvent(new CustomEvent("fable:refetch"));
    };
    document.addEventListener("visibilitychange", onVis);

    run();

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      controller && controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return { connected };
}
